import { RadioButton } from "@/Common/Components/Inputs/RadioButton";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { BaseObstetricalFileSubSection, BaseObstetricalFileSubSectionProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/Common/Subcomponent/BaseObstetricalFileSubSection";
import { ObstetricalResourcesPostnatalDepressionScale } from "@/Patient/Models/ObstetricalFile/Resources/ObstetricalResourcesPostnatalDepressionScale";
import { ObstetricalResourcesAnxietyScreeningSectionState } from "./ObstetricalResourcesAnxietyScreeningSection";

interface IObstetricalFileResourcesEdinburghPostnatalDepressionScales<TForm> extends BaseObstetricalFileSubSectionProps<TForm> {
    model: ObstetricalResourcesPostnatalDepressionScale;
    mutatePostnatalDepressionScaleWithFormUpdate: (mutation: (state: ObstetricalResourcesPostnatalDepressionScale) => void) => void;
    outerClassName?: string;
}

class ObstetricalFileResourcesEdinburghPostnatalDepressionScale<TForm> extends BaseObstetricalFileSubSection<TForm, IObstetricalFileResourcesEdinburghPostnatalDepressionScales<TForm>, ObstetricalResourcesAnxietyScreeningSectionState> {
    public constructor(props: IObstetricalFileResourcesEdinburghPostnatalDepressionScales<TForm>) {
        super(props);
    }

    public render(): JSX.Element {
        const edinburghScale = this.props.model;

        return (
            <div className={`row ${this.props.outerClassName ?? ""}`}>
                <div className="col-md-12">
                    <h4 className="text-bold">{this.getString("ObstetricalFile_EdinburghScaleLabel")}</h4>
                    <div className="flex-display flex-space-between flex-align-items-center resource-header-height">
                        <div className="resource-subheader-width">
                            <p className="text-bold f16px">{this.getString("ObstetricalFile_Past7DaysLabel")}</p>
                        </div>
                        <div className="flex-display flex-end flex-align-items-center">
                            <span className="margin-md-right">{this.getString("DateLabel")}</span>
                            <span className="margin-md-right" style={{ width: this.props.readonly ? "80px" : "120px" }}>
                                {this.controlRenderer.renderDateSelector({
                                    value: edinburghScale.PostnatalDepressionScaleDate,
                                    onChange: date => this.props.mutatePostnatalDepressionScaleWithFormUpdate(s => s.PostnatalDepressionScaleDate = date),
                                    id: "PostnatalDepressionScaleDate-date",
                                    className: "width-100"
                                })}
                            </span>
                            {!this.props.readonly && (
                                <a onClick={() => this.props.mutatePostnatalDepressionScaleWithFormUpdate(s => s.PostnatalDepressionScaleDate = new Date())}>
                                    <span>
                                        {this.getString("TodayLabel")}
                                    </span>
                                </a>
                            )}
                        </div>
                    </div>
                    {this.renderEdinburghScaleField(
                        "ObstetricalFile_AbilityToLaughQuestionLabel",
                        [
                            { label: "ObstetricalFile_AsMuchAsICouldLabel", pointValue: 0 },
                            { label: "ObstetricalFile_NotQuiteSoMuchLabel", pointValue: 1 },
                            { label: "ObstetricalFile_DefinitelyNotSoMuchLabel", pointValue: 2 },
                            { label: "ObstetricalFile_NotAtAllLabel", pointValue: 3 }
                        ],
                        value => this.props.mutatePostnatalDepressionScaleWithFormUpdate(s => s.AbilityToLaughScore = value),
                        edinburghScale.AbilityToLaughScore,
                        1
                    )}
                    {this.renderEdinburghScaleField(
                        "ObstetricalFile_HopefullnessQuestionLabel",
                        [
                            { label: "ObstetricalFile_AsMuchAsIEverLabel", pointValue: 0 },
                            { label: "ObstetricalFile_RatherLessThanIUsedLabel", pointValue: 1 },
                            { label: "ObstetricalFile_DefinitelyLessThanIUsedLabel", pointValue: 2 },
                            { label: "ObstetricalFile_HardlyAtAllLabel", pointValue: 3 }
                        ],
                        value => this.props.mutatePostnatalDepressionScaleWithFormUpdate(s => s.HopefullnessScore = value),
                        edinburghScale.HopefullnessScore,
                        2
                    )}
                    {this.renderEdinburghScaleField(
                        "ObstetricalFile_SelfBlameQuestionLabel",
                        [
                            { label: "ObstetricalFile_NoNeverLabel", pointValue: 0 },
                            { label: "ObstetricalFile_ANotVeryOftenLabel", pointValue: 1 },
                            { label: "ObstetricalFile_YesSomeOfTheTimeLabel", pointValue: 2 },
                            { label: "ObstetricalFile_YesMostOfTheTimeLabel", pointValue: 3 }
                        ],
                        value => this.props.mutatePostnatalDepressionScaleWithFormUpdate(s => s.SelfBlameScore = value),
                        edinburghScale.SelfBlameScore,
                        3
                    )}
                    {this.renderEdinburghScaleField(
                        "ObstetricalFile_AnxietyForNoReasonQuestionLabel",
                        [
                            { label: "ObstetricalFile_NoNotAtAllLabel", pointValue: 0 },
                            { label: "ObstetricalFile_HardlyEverLabel", pointValue: 1 },
                            { label: "ObstetricalFile_YesSometimesLabel", pointValue: 2 },
                            { label: "ObstetricalFile_YesVeryOftenLabel", pointValue: 3 }
                        ],
                        value => this.props.mutatePostnatalDepressionScaleWithFormUpdate(s => s.AnxietyScore = value),
                        edinburghScale.AnxietyScore,
                        4
                    )}
                    {this.renderEdinburghScaleField(
                        "ObstetricalFile_PanicForNoReasonQuestionLabel",
                        [
                            { label: "ObstetricalFile_NoNotAtAllLabel", pointValue: 0 },
                            { label: "ObstetricalFile_HardlyEverLabel", pointValue: 1 },
                            { label: "ObstetricalFile_YesSometimesLabel", pointValue: 2 },
                            { label: "ObstetricalFile_YesVeryOftenLabel", pointValue: 3 }
                        ],
                        value => this.props.mutatePostnatalDepressionScaleWithFormUpdate(s => s.PanicScore = value),
                        edinburghScale.PanicScore,
                        5
                    )}
                    {this.renderEdinburghScaleField(
                        "ObstetricalFile_CopingQuestionLabel",
                        [
                            { label: "ObstetricalFile_CopingScoreLabel", pointValue: 0 },
                            { label: "ObstetricalFile_CopedWellScoreLabel", pointValue: 1 },
                            { label: "ObstetricalFile_CopedAsWellAsUsualScoreLabel", pointValue: 2 },
                            { label: "ObstetricalFile_HaventBeenAbleToCopeScoreLabel", pointValue: 3 }
                        ],
                        value => this.props.mutatePostnatalDepressionScaleWithFormUpdate(s => s.CopingScore = value),
                        edinburghScale.CopingScore,
                        6
                    )}
                    {this.renderEdinburghScaleField(
                        "ObstetricalFile_DifficultySleepingQuestionLabel",
                        [
                            { label: "ObstetricalFile_NoNotMuchLabel", pointValue: 0 },
                            { label: "ObstetricalFile_NotveryOftenLabel", pointValue: 1 },
                            { label: "ObstetricalFile_YesSometimesLabel", pointValue: 2 },
                            { label: "ObstetricalFile_YesMostOfTheTimeLabel", pointValue: 3 }
                        ],
                        value => this.props.mutatePostnatalDepressionScaleWithFormUpdate(s => s.DifficultySleepingScore = value),
                        edinburghScale.DifficultySleepingScore,
                        7
                    )}
                    {this.renderEdinburghScaleField(
                        "ObstetricalFile_SadnessQuestionLabel",
                        [
                            { label: "ObstetricalFile_NoNotMuchLabel", pointValue: 0 },
                            { label: "ObstetricalFile_NotveryOftenLabel", pointValue: 1 },
                            { label: "ObstetricalFile_YesQuiteOftenLabel", pointValue: 2 },
                            { label: "ObstetricalFile_YesMostOfTheTimeLabel", pointValue: 3 }
                        ],
                        value => this.props.mutatePostnatalDepressionScaleWithFormUpdate(s => s.SadnessScore = value),
                        edinburghScale.SadnessScore,
                        8
                    )}
                    {this.renderEdinburghScaleField(
                        "ObstetricalFile_CryingQuestionLabel",
                        [
                            { label: "ObstetricalFile_NoNeverLabel", pointValue: 0 },
                            { label: "ObstetricalFile_OnlyOccasionallyLabel", pointValue: 1 },
                            { label: "ObstetricalFile_YesQuiteOftenLabel", pointValue: 2 },
                            { label: "ObstetricalFile_YesMostOfTheTimeLabel", pointValue: 3 }
                        ],
                        value => this.props.mutatePostnatalDepressionScaleWithFormUpdate(s => s.CryingScore = value),
                        edinburghScale.CryingScore,
                        9
                    )}
                    {this.renderEdinburghScaleField(
                        "ObstetricalFile_SelfHarmQuestionLabel",
                        [
                            { label: "ObstetricalFile_NoNeverLabel", pointValue: 0 },
                            { label: "ObstetricalFile_OnlyOccasionallyLabel", pointValue: 1 },
                            { label: "ObstetricalFile_YesQuiteOftenLabel", pointValue: 2 },
                            { label: "ObstetricalFile_YesMostOfTheTimeLabel", pointValue: 3 }
                        ],
                        value => this.props.mutatePostnatalDepressionScaleWithFormUpdate(s => s.SelfHarmScore = value),
                        edinburghScale.SelfHarmScore,
                        10
                    )}
                </div>
            </div>

        );
    }

    private renderEdinburghScaleField(label: string, columns: { label: string, pointValue: number }[], onChange: (value: number) => void, selectedValue: number, row: number): JSX.Element {
        if (columns.length < 4)
            return null;

        // Grid css render row by row. So we need to reorder them so they are rendered properly.
        const questions = [columns[0], columns[2], columns[1], columns[3]];

        return (
            <div className={"row padding-xs-bottom " + (row !== 1 ? "border border-top small-width padding-xs-top" : "")}>
                <div className="col-md-5">
                    <p className="text-bold">{row}. {this.getString(label)}</p>
                </div>
                <div className="col-md-7 resource-edinburgh-radio-buttons">
                    {questions.map((p, index) => (
                        <RadioButton
                            key={`EdinburghPerinatalQuestionKey${label}_${p.label}_${p.pointValue}_${row}`}
                            radioInputGroupId={`EdinburghPerinatalQuestion${index}_${row}`}
                            selectedItem={selectedValue}
                            items={Immutable.List([new KeyValuePair(this.getString(p.label), p.pointValue)])}
                            onChange={item => onChange(item.Value)}
                            disabled={this.props.readonly}
                        />
                    ))}
                </div>
            </div>
        );
    }
}

export { ObstetricalFileResourcesEdinburghPostnatalDepressionScale };
